import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { formatCurrency } from '../utils/currency';

interface Invoice {
  id: string;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  status: string;
  totalAmount: number;
  taxAmountInPln?: number;
  currency: string;
  clientName?: string;
  bankAccountName?: string;
  bankAccountBankName?: string;
  bankAccountNumber?: string;
  invoiceType?: string;
  notes?: string;
  internalComment?: string;
  wfirmaId?: number | null;
  wfirmaInvoiceNumber?: string | null;
  ksefNumber?: string | null;
  client?: {
    id: string;
    name: string;
  };
}

interface InvoiceTableProps {
  invoices: Invoice[];
  companyId?: string;
  onEdit?: (invoice: Invoice) => void;
  onView?: (invoice: Invoice) => void;
  onDownload?: (invoice: Invoice) => void;
  onDownloadWfirmaPdf?: (invoice: Invoice) => void;
  onCopy?: (invoice: Invoice) => void;
  onStatusChange?: (invoiceId: string, newStatus: string) => void;
  showClient?: boolean;
}

// Простой компонент для tooltip
const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children}
      {showTooltip && (
        <div className="absolute z-50 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg whitespace-nowrap bottom-full left-1/2 transform -translate-x-1/2 mb-2">
          {text}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
            <div className="border-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
};

const getStatusText = (status: string, t: any) => {
  switch (status) {
    case 'DRAFT':
      return t('invoices.statuses.DRAFT');
    case 'SENT':
      return t('invoices.statuses.SENT');
    case 'KORYGOWANA':
      return t('invoices.statuses.KORYGOWANA');
    case 'PARTIALLY_PAID':
      return t('invoices.statuses.PARTIALLY_PAID');
    case 'PAID':
      return t('invoices.statuses.PAID');
    case 'OVERDUE':
      return t('invoices.statuses.OVERDUE');
    case 'CANCELLED':
      return t('invoices.statuses.CANCELLED');
    default:
      return status;
  }
};

const getStatusClass = (status: string) => {
  switch (status) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-700';
    case 'SENT':
      return 'bg-blue-50 text-blue-700';
    case 'KORYGOWANA':
      return 'bg-purple-50 text-purple-700';
    case 'PARTIALLY_PAID':
      return 'bg-yellow-50 text-yellow-700';
    case 'PAID':
      return 'bg-green-50 text-green-700';
    case 'OVERDUE':
      return 'bg-red-50 text-red-700';
    case 'CANCELLED':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('ru-RU', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric' 
  });
};

const getLastFourDigits = (accountNumber?: string) => {
  if (!accountNumber) return '';
  const digits = accountNumber.replace(/\D/g, '');
  return digits.slice(-4);
};

const getInvoiceTypePrefix = (invoiceType?: string) => {
  if (!invoiceType) return '';
  switch (invoiceType) {
    case 'STANDARD':
      return 'FV';
    case 'PROFORMA':
      return 'Pro';
    case 'CORRECTION':
      return 'Kor';
    default:
      return '';
  }
};

export function InvoiceTable({
  invoices,
  companyId,
  onEdit,
  onView,
  onDownload,
  onDownloadWfirmaPdf,
  onCopy,
  onStatusChange,
  showClient = true
}: InvoiceTableProps) {
  const { t } = useTranslation();
  
  if (invoices.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('companies.invoices.noInvoicesFound')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="w-32 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('invoices.invoiceNumber') || 'Номер'}
            </th>
            {showClient && (
              <th className="w-48 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('companies.invoices.client') || 'Клиент'}
              </th>
            )}
            <th className="w-28 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('invoices.issueDate') || 'Дата'}
            </th>
            <th className="w-36 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('invoices.grandTotal') || 'Сумма'}
            </th>
            <th className="w-32 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('companies.bankAccounts.title') || 'Банк'}
            </th>
            <th className="w-32 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('invoices.status') || 'Статус'}
            </th>
            <th className="w-20 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              KSeF
            </th>
            <th className="w-40 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('invoices.internalComment') || 'Внутренний комментарий'}
            </th>
            <th className="w-40 px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('common.actions') || 'Действия'}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {invoices.map((invoice) => (
            <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
              {/* Номер инвойса */}
              <td className="px-4 py-3 whitespace-nowrap overflow-hidden">
                <div className="flex items-center gap-2">
                  {getInvoiceTypePrefix(invoice.invoiceType) && (
                    <span className="text-xs text-gray-500 font-medium">
                      {getInvoiceTypePrefix(invoice.invoiceType)}
                    </span>
                  )}
                  {companyId ? (
                    <Link
                      to={`/companies/${companyId}/invoices/${invoice.id}`}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline truncate"
                      title={t('invoices.viewDetails') || 'Просмотр деталей'}
                    >
                      {invoice.invoiceNumber}
                    </Link>
                  ) : (
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {invoice.invoiceNumber}
                    </span>
                  )}
                </div>
              </td>

              {/* Клиент */}
              {showClient && (
                <td className="px-4 py-3 overflow-hidden">
                  <div className="text-sm text-gray-900 truncate" title={invoice.clientName || invoice.client?.name || ''}>
                    {invoice.clientName || invoice.client?.name || '-'}
                  </div>
                </td>
              )}

              {/* Дата выпуска */}
              <td className="px-4 py-3 whitespace-nowrap overflow-hidden">
                <div className="text-sm text-gray-900">{formatDate(invoice.issueDate)}</div>
              </td>

              {/* Сумма */}
              <td className="px-4 py-3 whitespace-nowrap overflow-hidden">
                <div className="text-sm font-medium text-gray-900">
                  {formatCurrency(invoice.totalAmount, invoice.currency)}
                </div>
                {invoice.currency !== 'PLN' && 
                 invoice.taxAmountInPln != null && 
                 typeof invoice.taxAmountInPln === 'number' && 
                 invoice.taxAmountInPln > 0 && (
                  <div className="text-xs text-gray-500">
                    {t('invoices.exchangeRates.vatInPln')}: {formatCurrency(invoice.taxAmountInPln, 'PLN')}
                  </div>
                )}
              </td>

              {/* Банковский счет */}
              <td className="px-4 py-3 overflow-hidden">
                {invoice.bankAccountName ? (
                  <div className="text-sm text-gray-900">
                    <div className="truncate" title={invoice.bankAccountName}>{invoice.bankAccountName}</div>
                    {invoice.bankAccountNumber && (
                      <div className="text-xs text-gray-500 font-mono">
                        ...{getLastFourDigits(invoice.bankAccountNumber)}
                      </div>
                    )}
                  </div>
                ) : invoice.bankAccountBankName ? (
                  <div className="text-sm text-gray-900">
                    <div className="truncate" title={invoice.bankAccountBankName}>{invoice.bankAccountBankName}</div>
                    {invoice.bankAccountNumber && (
                      <div className="text-xs text-gray-500 font-mono">
                        ...{getLastFourDigits(invoice.bankAccountNumber)}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">-</span>
                )}
              </td>

              {/* Статус */}
              <td className="px-4 py-3 whitespace-nowrap overflow-hidden">
                {onStatusChange ? (
                  <select
                    value={invoice.status}
                    onChange={(e) => onStatusChange(invoice.id, e.target.value)}
                    className={`w-full px-2 py-1 text-xs font-medium rounded border-0 focus:ring-1 focus:ring-blue-500 focus:outline-none cursor-pointer ${getStatusClass(invoice.status)}`}
                    title={t('invoices.changeStatus') || 'Изменить статус'}
                  >
                    <option value="DRAFT">{t('invoices.statuses.DRAFT')}</option>
                    <option value="SENT">{t('invoices.statuses.SENT')}</option>
                    <option value="KORYGOWANA">{t('invoices.statuses.KORYGOWANA')}</option>
                    <option value="PARTIALLY_PAID">{t('invoices.statuses.PARTIALLY_PAID')}</option>
                    <option value="PAID">{t('invoices.statuses.PAID')}</option>
                    <option value="OVERDUE">{t('invoices.statuses.OVERDUE')}</option>
                    <option value="CANCELLED">{t('invoices.statuses.CANCELLED')}</option>
                  </select>
                ) : (
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusClass(invoice.status)}`}>
                    {getStatusText(invoice.status, t)}
                  </span>
                )}
              </td>

              {/* KSeF: если есть номер — показываем "галочку" для скачивания PDF из wFirma */}
              <td className="px-4 py-3 whitespace-nowrap text-center overflow-hidden">
                {invoice.wfirmaId != null && invoice.ksefNumber?.trim() ? (
                  <Tooltip text={t('invoices.downloadWfirmaPdf') || 'Скачать PDF из wFirma'}>
                    <button
                      type="button"
                      onClick={() => onDownloadWfirmaPdf?.(invoice)}
                      className="inline-flex items-center justify-center rounded p-1.5 text-green-600 hover:bg-green-50 transition-colors"
                      aria-label={t('invoices.downloadWfirmaPdf') || 'Скачать PDF из wFirma'}
                      title={invoice.ksefNumber}
                    >
                      <span className="text-base">✅</span>
                    </button>
                  </Tooltip>
                ) : (
                  <span className="text-xs text-gray-300">—</span>
                )}
              </td>

              {/* Внутренний комментарий */}
              <td className="px-4 py-3 overflow-hidden">
                <div className="text-sm text-gray-900 truncate" title={invoice.internalComment || ''}>
                  {invoice.internalComment || '-'}
                </div>
              </td>

              {/* Действия */}
              <td className="px-4 py-3 whitespace-nowrap text-right overflow-hidden">
                <div className="flex items-center justify-end gap-1">
                  {onView && (
                    <Tooltip text={t('invoices.viewPdf') || 'Просмотр PDF'}>
                      <button
                        onClick={() => onView(invoice)}
                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                      >
                        <span className="text-base">👁️</span>
                      </button>
                    </Tooltip>
                  )}
                  {onEdit && (
                    <Tooltip text={t('invoices.edit') || 'Редактировать'}>
                      <button
                        onClick={() => onEdit(invoice)}
                        className="p-1.5 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                      >
                        <span className="text-base">✏️</span>
                      </button>
                    </Tooltip>
                  )}
                  {onDownload && (
                    <Tooltip text={t('invoices.download') || 'Скачать'}>
                      <button
                        onClick={() => onDownload(invoice)}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                      >
                        <span className="text-base">⬇️</span>
                      </button>
                    </Tooltip>
                  )}
                  {onCopy && (
                    <Tooltip text={t('invoices.copy') || 'Копировать'}>
                      <button
                        onClick={() => onCopy(invoice)}
                        className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                      >
                        <span className="text-base">📋</span>
                      </button>
                    </Tooltip>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
